'use client';

import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { getTourStyles } from '@/components/tour/tourStyles';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import Pagination from '@/components/pages/shared/Pagination';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

import { Plus, UserPlus, Trash2, Users, SearchX, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import Joyride from 'react-joyride';
import getClientsTourSteps from '@/components/tour/shared/ClientsTourSteps';

import AddClientDialog from '@/components/pages/dialogs/AddClientDialog';
import { useClientes } from '@/components/hooks/useClientes';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.2 } },
};

type Cliente = {
  id_usuario: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  ultima_cita?: string | null;
};

type SortKey = 'nombre' | 'email' | 'telefono' | 'ultima_cita';

export default function Clients() {
  const [empresaId, setEmpresaId] = useState('');
  const isMobile = window.innerWidth < 640;
  const {
    getClientesEmpresa,
    getClientesRecientes,
    agregarCliente,
    borrarCliente,
  } = useClientes();

  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [recientes, setRecientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRecientes, setLoadingRecientes] = useState(true);

  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey>('nombre');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Cliente | null>(null);

  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    setEmpresaId(localStorage.getItem('id_empresa') || '');
  }, []);

  const fetchClientes = async (id: string) => {
    setLoading(true);
    const data = await getClientesEmpresa(id);
    setClientes(data);
    setLoading(false);
  };

  const fetchRecientes = async (id: string) => {
    setLoadingRecientes(true);
    const data = await getClientesRecientes(id);
    setRecientes(data);
    setLoadingRecientes(false);
  };

  useEffect(() => {
    if (!empresaId) return;
    fetchClientes(empresaId);
    fetchRecientes(empresaId);
  }, [empresaId]);

  const hayRecientes = !loadingRecientes && recientes.length > 0;

  const steps = getClientsTourSteps(isMobile, hayRecientes);

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta
  // página. Se oculta mientras carga, que es cuando aún no hay tabla que
  // enseñar.
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  const handleDeleteCliente = async (cliente: Cliente) => {
    if (!empresaId) return;
    await borrarCliente(empresaId, cliente.id_usuario, () => {
      fetchClientes(empresaId);
      fetchRecientes(empresaId);
    });
  };

  const handleAddCliente = async (cliente: Cliente) => {
    if (!empresaId) return;
    await agregarCliente(empresaId, cliente.email || '', null, () => {
      fetchClientes(empresaId);
      fetchRecientes(empresaId);
    });
  };

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
    setPage(1);
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (key: SortKey) =>
    sortKey === key ? (sortOrder === 'asc' ? '▲' : '▼') : null;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return clientes;
    return clientes.filter((c) => {
      const nombre = (c.nombre || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      const telefono = (c.telefono || '').toLowerCase();
      return nombre.includes(q) || email.includes(q) || telefono.includes(q);
    });
  }, [clientes, filter]);

  const sorted = useMemo(() => {
    const data = [...filtered];
    data.sort((a, b) => {
      const aVal = (a as any)[sortKey] ?? '';
      const bVal = (b as any)[sortKey] ?? '';
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filtered, sortKey, sortOrder]);

  const totalPages = useMemo(
    () => Math.ceil(sorted.length / pageSize) || 1,
    [sorted.length, pageSize]
  );

  const currentData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-8 pb-28 lg:pb-0"
    >
      {/* Header — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants}>
        <div className="tour-title flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Clientes
          </span>
        </div>
      </motion.div>

      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped')
            setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* Clientes sugeridos */}
      {hayRecientes && (
        <motion.div
          variants={itemVariants}
          className="space-y-3 tour-recientes"
        >
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
            Últimos clientes que reservaron (no añadidos)
          </h2>

          {recientes.map((c) => (
            <Card
              key={c.id_usuario}
              className={`p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 rounded-xl ${GLASS_CARD}`}
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 flex items-center justify-center text-[#103254] dark:text-blue-400 font-semibold text-lg flex-shrink-0">
                  {(c.nombre?.[0] || '?').toUpperCase()}
                </div>

                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {c.nombre || 'Sin nombre'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {c.email || '-'}
                  </p>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="rounded-full flex items-center gap-1 w-full sm:w-auto dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10"
                onClick={() => handleAddCliente(c)}
              >
                <UserPlus className="w-4 h-4" /> Añadir
              </Button>
            </Card>
          ))}
        </motion.div>
      )}

      {/* Filter + Add */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row gap-3 justify-between items-center tour-filter-add"
      >
        <Input
          type="text"
          placeholder="Buscar clientes por nombre, email o teléfono"
          value={filter}
          onChange={handleFilterChange}
          className="tour-filter-input w-full sm:w-1/2 dark:bg-white/5 dark:border-white/10 dark:text-white"
        />

        {clientes.length > 0 && (
          <Button
            onClick={() => setOpenAddDialog(true)}
            className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 tour-add-btn w-full sm:w-auto transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" /> Añadir cliente
          </Button>
        )}
      </motion.div>

      {/* Tabla / Estados */}
      <AnimatePresence>
        {loading ? (
          <motion.div
            variants={itemVariants}
            className="flex justify-center items-center h-32"
          >
            <motion.div
              variants={rowVariants}
              className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#103254]"
            />
          </motion.div>
        ) : clientes.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <Users className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Sin clientes
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">
                Aún no has agregado ningún cliente.
              </p>
              <Button
                onClick={() => setOpenAddDialog(true)}
                className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" /> Añadir cliente
              </Button>
            </CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <SearchX className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Sin coincidencias
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No se encontraron clientes con ese criterio.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Vista móvil - Tarjetas */}
            <div className="block lg:hidden space-y-4">
              {currentData.map((c) => (
                <motion.div
                  key={c.id_usuario}
                  variants={rowVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Card className={`rounded-xl ${GLASS_CARD}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 flex items-center justify-center text-[#103254] dark:text-blue-400 font-semibold text-lg flex-shrink-0">
                            {(c.nombre?.[0] || '?').toUpperCase()}
                          </div>

                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-base truncate">
                              {c.nombre || 'Sin nombre'}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {c.email || '-'}
                            </p>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(c)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 rounded-full ml-2 flex-shrink-0 tour-delete-btn-mobile hover:bg-red-50 dark:hover:bg-red-900/20"
                          aria-label="Eliminar cliente"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      {c.telefono && (
                        <div className="flex items-center gap-2 text-sm pt-2 border-t border-gray-200 dark:border-white/10">
                          <span className="text-gray-500 dark:text-gray-400">
                            Teléfono:
                          </span>
                          <span className="text-gray-800 dark:text-white">
                            {c.telefono}
                          </span>
                        </div>
                      )}

                      {c.ultima_cita && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            Última cita:
                          </span>
                          <span className="text-gray-800 dark:text-white">
                            {new Date(c.ultima_cita).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Vista desktop - Tabla */}
            <Card className={`hidden lg:block rounded-2xl ${GLASS_CARD}`}>
              <CardContent className="overflow-x-auto p-0">
                <table className="min-w-full table-auto text-sm">
                  <thead className="tour-table-head bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 uppercase text-xs sticky top-0 z-10">
                    <tr>
                      <th
                        onClick={() => handleSort('nombre')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        Nombre {renderSortIcon('nombre')}
                      </th>
                      <th
                        onClick={() => handleSort('email')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        Email {renderSortIcon('email')}
                      </th>
                      <th
                        onClick={() => handleSort('telefono')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        Teléfono {renderSortIcon('telefono')}
                      </th>
                      <th
                        onClick={() => handleSort('ultima_cita')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        Última cita {renderSortIcon('ultima_cita')}
                      </th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                    {currentData.map((c, idx) => (
                      <motion.tr
                        key={c.id_usuario}
                        variants={rowVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className={
                          idx % 2 === 0
                            ? 'bg-white dark:bg-transparent'
                            : 'bg-gray-50 dark:bg-white/[0.03]'
                        }
                      >
                        <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">
                          {c.nombre || 'Sin nombre'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {c.email || '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {c.telefono || '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {c.ultima_cita
                            ? new Date(c.ultima_cita).toLocaleDateString()
                            : '-'}
                        </td>

                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteTarget(c)}
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 rounded-full tour-delete-btn-desktop hover:bg-red-50 dark:hover:bg-red-900/20"
                              aria-label="Eliminar cliente"
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Paginación */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={sorted.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={changePageSize}
              itemLabel="clientes"
              className="tour-pagination"
            />
          </>
        )}
      </AnimatePresence>

      {/* Confirmar eliminación (bottom sheet) */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-red-600 dark:text-red-400 pr-10">
                Eliminar cliente
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
                Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <button
              onClick={() => setDeleteTarget(null)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                ¿Seguro que quieres eliminar a{' '}
                <strong className="text-gray-900 dark:text-white">
                  {deleteTarget?.nombre || 'Sin nombre'}
                </strong>{' '}
                de tu empresa?
              </p>
            </div>
          </div>

          <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
            >
              Cancelar
            </Button>

            <Button
              onClick={async () => {
                if (!deleteTarget) return;
                await handleDeleteCliente(deleteTarget);
                setDeleteTarget(null);
              }}
              className={`w-full sm:flex-1 ${DLG_BTN_DANGER}`}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddClientDialog
        open={openAddDialog}
        onOpenChange={setOpenAddDialog}
        onAdded={() => {
          if (!empresaId) return;
          fetchClientes(empresaId);
          fetchRecientes(empresaId);
        }}
      />
    </motion.div>
  );
}